import { zodResolver } from '@hookform/resolvers/zod';
import { ExternalLink } from 'lucide-react';
import { useId, useLayoutEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { careerSiteSettingsSchema, type CareerSiteSettings, type CareerSiteSettingsView } from '@nest-recruit/shared';
import { Button, buttonClassName } from '../../../components/Button';
import { Field } from '../../../components/Field';
import { Input, Textarea } from '../../../components/Input';
import { SkeletonLines } from '../../../components/Skeleton';
import { Tabs } from '../../../components/Tabs';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { useMediaQuery } from '../../../lib/use-media-query';
import { useUnsavedWarning } from '../../../lib/use-unsaved-warning';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useCareerSiteSettings, useChangeCareerSite } from '../api/publishing-api';
import { AccentSwatches } from '../components/career-site/AccentSwatches';
import { changedSettings, formValuesOf, previewDraftOf, rebasedSettings, savedShapeOf, type WatchedSettings } from '../components/career-site/career-site-form';
import { LogoRow } from '../components/career-site/LogoRow';
import { SectionsEditor } from '../components/career-site/SectionsEditor';
import { SitePreview } from '../components/career-site/SitePreview';
import styles from './CareerSitePage.module.css';

/** At this width and below the preview moves behind a "Preview" tab (the drawing's 1024). */
const ONE_COLUMN_QUERY = '(max-width: 1180px)';

type Pane = 'settings' | 'preview';

const PANES = [
  { value: 'settings', label: 'Settings' },
  { value: 'preview', label: 'Preview' },
] as const;

interface Base {
  /** The version a save is checked against: the one this Admin started from. */
  readonly version: number;
  /** What "unsaved" is measured from. */
  readonly settings: CareerSiteSettings;
}

/**
 * ADMIN-12 — career site branding (master plan S13), one site for every venture (DECISIONS-LOG S13 Q6). Admin redesign
 * 2026-11-02, Option A of `4-messages/career-site.html`: the settings on the left in the order the site reads, and the
 * live site on the right, drawn by the public page's own code with today's open roles. The accent is a Flock token,
 * never a typed colour. The logo saves at once; everything else waits for Save.
 */
export function CareerSitePage(): ReactNode {
  const canView = useCanView('hiring_setup');
  const settings = useCareerSiteSettings(canView);
  if (!canView) return <ForbiddenState resource="the career site settings" contact="Your admin" />;
  if (settings.isPending) {
    return (
      <div className={styles.page}>
        <SkeletonLines count={10} />
      </div>
    );
  }
  if (settings.error) return <ServerErrorState onRetry={() => void settings.refetch()} />;
  // Never keyed on the version: a save by someone else must not throw away what this Admin has typed.
  return <CareerSiteEditor view={settings.data} />;
}

function CareerSiteEditor({ view }: { readonly view: CareerSiteSettingsView }): ReactNode {
  const canEdit = useCanEdit('hiring_setup');
  const save = useChangeCareerSite();
  const show = useToasts((state) => state.show);
  const formId = useId();
  const colourLabelId = useId();
  const isOneColumn = useMediaQuery(ONE_COLUMN_QUERY);
  const [pane, setPane] = useState<Pane>('settings');
  const [base, setBase] = useState<Base>(() => ({ version: view.version, settings: formValuesOf(view) }));
  // What was last sent, so the answer to our own save is never taken for someone else's.
  const [submitted, setSubmitted] = useState<CareerSiteSettings | null>(null);
  const form = useForm<CareerSiteSettings>({ resolver: zodResolver(careerSiteSettingsSchema), mode: 'onBlur', defaultValues: base.settings });
  const sections = useFieldArray({ control: form.control, name: 'sections' });
  const values: WatchedSettings = useWatch({ control: form.control });
  const changed = changedSettings(values, base.settings);
  const isDirty = changed.length > 0;
  useUnsavedWarning(isDirty);

  // A newer version arrived. Our own save, an untouched form, or one that already reads the same: stand on it (an
  // untouched form also shows theirs). An edited form keeps its edits and says so below; the server refuses a save
  // from the old version, so nothing of theirs is overwritten unseen.
  const incoming = formValuesOf(view);
  const isOurs = submitted !== null && changedSettings(incoming, submitted).length === 0;
  const isAlreadySame = changedSettings(values, incoming).length === 0;
  const isNewer = view.version !== base.version;
  const isOutdated = isNewer && !isOurs && isDirty && !isAlreadySame;
  // State that follows the server, set while rendering (React's way to derive state from a prop); the form itself is
  // reset before the screen paints, so an untouched form never flashes "unsaved".
  const [resetTo, setResetTo] = useState<CareerSiteSettings | null>(null);
  if (isNewer && !isOutdated) {
    setBase({ version: view.version, settings: incoming });
    if (isOurs) setSubmitted(null);
    if (!isOurs && !isDirty) setResetTo(incoming);
  }
  useLayoutEffect(() => {
    if (resetTo) form.reset(resetTo);
  }, [resetTo, form]);

  /** Their version becomes the base; what this Admin changed stays theirs (review W3). */
  const bringInTheirs = (): void => {
    form.reset(rebasedSettings(savedShapeOf(form.getValues()), base.settings, incoming));
    setBase({ version: view.version, settings: incoming });
  };

  const submit = form.handleSubmit(
    async (input) => {
      const body = savedShapeOf(input);
      setSubmitted(body);
      try {
        await save.mutateAsync({ kind: 'settings', body: { ...body, version: base.version } });
        show({ tone: 'success', message: 'Career site saved', detail: 'Visitors see the change within a minute.' });
      } catch (cause) {
        setSubmitted(null);
        show({ tone: 'danger', message: "Couldn't save the career site", detail: errorMessage(cause) });
      }
    },
    // A field to fix: on a narrow screen the settings may be behind the Preview tab, so bring them forward.
    () => setPane('settings'),
  );

  /** 06-qol §2: every form saves on Ctrl or Cmd + Enter. */
  const onKeyDown = (event: KeyboardEvent<HTMLFormElement>): void => {
    if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey)) return;
    event.preventDefault();
    if (canEdit && isDirty && !isOutdated) void submit();
  };

  const errors = form.formState.errors;
  const settingsPane = (
    <form id={formId} className={styles.settings} onSubmit={(event) => void submit(event)} onKeyDown={onKeyDown} noValidate>
      <fieldset className={styles.fieldset} disabled={!canEdit}>
        <section className={styles.group} aria-labelledby={`${formId}-look`}>
          <h2 id={`${formId}-look`} className={styles.groupTitle}>
            Look
          </h2>
          <LogoRow view={view} siteTitle={values.siteTitle ?? view.siteTitle} canEdit={canEdit} />
          <Field label="Site name" isRequired error={errors.siteTitle?.message}>
            {({ inputId, describedBy, isInvalid }) => (
              <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} maxLength={80} {...form.register('siteTitle')} />
            )}
          </Field>
          <div className={styles.colour}>
            <span id={colourLabelId} className={styles.label}>
              Colour
            </span>
            <Controller
              control={form.control}
              name="accent"
              render={({ field }) => <AccentSwatches labelId={colourLabelId} value={field.value} onChange={field.onChange} disabled={!canEdit} />}
            />
          </div>
        </section>
        <section className={styles.group} aria-labelledby={`${formId}-front`}>
          <h2 id={`${formId}-front`} className={styles.groupTitle}>
            Front page
          </h2>
          <Field label="Headline" isRequired error={errors.headline?.message}>
            {({ inputId, describedBy, isInvalid }) => (
              <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} maxLength={120} {...form.register('headline')} />
            )}
          </Field>
          <Field label="Introduction" isOptional hint="A blank line starts a new paragraph." error={errors.intro?.message}>
            {({ inputId, describedBy, isInvalid }) => (
              <Textarea
                id={inputId}
                aria-describedby={describedBy}
                isInvalid={isInvalid}
                rows={4}
                maxLength={2000}
                {...form.register('intro', { setValueAs: (value: string | null) => (value === null || value.trim() === '' ? null : value) })}
              />
            )}
          </Field>
        </section>
        <section className={styles.group}>
          <SectionsEditor form={form} sections={sections} canEdit={canEdit} />
        </section>
        <section className={styles.group} aria-labelledby={`${formId}-footer`}>
          <h2 id={`${formId}-footer`} className={styles.groupTitle}>
            Footer
          </h2>
          <Field label="Contact email" isOptional hint="Shown at the foot of every page." error={errors.contactEmail?.message}>
            {({ inputId, describedBy, isInvalid }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                isInvalid={isInvalid}
                type="email"
                {...form.register('contactEmail', { setValueAs: (value: string | null) => (value === null || value.trim() === '' ? null : value) })}
              />
            )}
          </Field>
        </section>
      </fieldset>
    </form>
  );
  const previewPane = <SitePreview draft={previewDraftOf(values)} siteUrl={view.siteUrl} isUnsaved={isDirty} />;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <h1 className={styles.title}>Career site</h1>
          <p className={styles.lede}>What candidates see first. One site lists every venture's open roles; visitors see a saved change within a minute.</p>
        </div>
        <div className={styles.headActions}>
          <a className={buttonClassName('outline')} href={view.siteUrl} target="_blank" rel="noreferrer">
            <ExternalLink size={16} aria-hidden />
            Open the site
          </a>
          {canEdit && (
            <Button
              variant="solid"
              type="submit"
              form={formId}
              isPending={save.isPending}
              disabled={!isDirty || isOutdated}
              aria-label={isDirty ? `Save — ${changed.length} unsaved ${changed.length === 1 ? 'change' : 'changes'}` : 'Save'}
            >
              Save
              {isDirty && <span className={styles.unsavedDot} aria-hidden />}
            </Button>
          )}
        </div>
      </header>
      {canEdit && (
        <SaveStatus
          changed={changed}
          isOutdated={isOutdated}
          isPending={save.isPending}
          onDiscard={() => form.reset(base.settings)}
          onBringIn={bringInTheirs}
        />
      )}
      {isOneColumn ? (
        <Tabs label="Career site" tabs={PANES} value={pane} onChange={setPane}>
          <div hidden={pane !== 'settings'}>{settingsPane}</div>
          <div hidden={pane !== 'preview'}>{previewPane}</div>
        </Tabs>
      ) : (
        <div className={styles.columns}>
          {settingsPane}
          <div className={styles.previewColumn}>{previewPane}</div>
        </div>
      )}
    </div>
  );
}

function SaveStatus({
  changed,
  isOutdated,
  isPending,
  onDiscard,
  onBringIn,
}: {
  readonly changed: readonly string[];
  readonly isOutdated: boolean;
  readonly isPending: boolean;
  readonly onDiscard: () => void;
  readonly onBringIn: () => void;
}): ReactNode {
  if (isOutdated) {
    return (
      <div className={styles.status} role="status">
        <span className={styles.outdated}>Someone else saved the career site while you were changing it. Bring in their version — your changes stay — then save.</span>
        <Button size="sm" variant="outline" onClick={onBringIn}>
          Bring in their version
        </Button>
      </div>
    );
  }
  if (changed.length === 0) {
    return (
      <div className={styles.status} role="status">
        Everything is saved
      </div>
    );
  }
  return (
    <div className={styles.status} role="status">
      <span>
        <strong className={styles.strong}>
          {changed.length} unsaved {changed.length === 1 ? 'change' : 'changes'}
        </strong>
        {' · '}
        {changed.join(', ')}
      </span>
      <Button size="sm" variant="quiet" disabled={isPending} onClick={onDiscard}>
        Discard
      </Button>
    </div>
  );
}
